import { IconChevronLeft, IconChevronRight } from '@/theme/icons';
import { isThisYear } from '@/utils/calendar';
import { getDateFnsLocale } from '@/utils/dateLocale';
import rem from '@/utils/rem';
import {
  Box,
  Flex,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Tooltip,
  useDisclosure,
} from '@chakra-ui/react';
import { addYears, format, subYears } from 'date-fns';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  selectedDate: Date;
  updateSelectedDate: (date: Date) => void;
  showDatepicker?: boolean;
  onCategorySpotlight?: boolean;
};

const YearNavigator = ({
  selectedDate,
  updateSelectedDate,
  showDatepicker = false,
  onCategorySpotlight = false,
}: Props) => {
  const { t, i18n } = useTranslation('CategorySpotlight');
  const {
    isOpen: isDatePickerOpen,
    onOpen: onDatePickerOpen,
    onClose: onDatePickerClose,
  } = useDisclosure({ id: 'datepicker-year-navigation' });

  const locale = getDateFnsLocale(i18n.language);

  // Color logic for Category Spotlight
  const getBorderColor = () => {
    if (!onCategorySpotlight) return 'stroke-primary';

    const currentDate = new Date();
    const isCurrentYear = isThisYear(selectedDate);
    const isFutureYear = selectedDate.getFullYear() > currentDate.getFullYear();

    if (isCurrentYear) return 'green.400'; // Current year - green
    if (isFutureYear) return 'yellow.400'; // Future year - yellow
    return 'red.400'; // Past year - red
  };

  const onHandlePrevNextCalendarNavigation = useCallback(
    (date: Date, action: 'PREV' | 'NEXT') => {
      switch (action) {
        case 'PREV': {
          const newDate = subYears(date, 1);
          updateSelectedDate(newDate);
          return newDate;
        }
        case 'NEXT': {
          const newDate = addYears(date, 1);
          updateSelectedDate(newDate);
          return newDate;
        }
        default: {
          updateSelectedDate(date);
          return date;
        }
      }
    },
    [updateSelectedDate],
  );

  const currentYear = format(selectedDate, 'yyyy', { locale });

  if (showDatepicker) {
    return (
      <HStack
        position="relative"
        justifyContent="center"
        gap={rem(5)}
        width="fit-content"
        minWidth="fit-content"
        height="full"
        paddingInlineEnd={rem(8)}
        paddingInlineStart={rem(8)}
      >
        <Tooltip label={t('yearNavigator.previousYear')} placement="top">
          <IconButton
            width={rem(32)}
            minWidth={0}
            height="full"
            margin={0}
            padding={0}
            borderWidth={rem(1)}
            borderStyle="solid"
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            aria-label={t('yearNavigator.previousYear')}
            backgroundColor="background-primary"
            icon={<Icon as={IconChevronLeft} boxSize={rem(14)} />}
            onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, 'PREV')}
            size="xs"
            variant="ghost"
          />
        </Tooltip>

        <Popover closeOnBlur closeOnEsc isLazy isOpen={isDatePickerOpen} onClose={onDatePickerClose} placement="bottom">
          <PopoverTrigger>
            <Text
              textStyle={{ base: 'mediumBlack', lg: 'largeBlack' }}
              alignItems="center"
              justifyContent="center"
              display="flex"
              minWidth={rem(80)}
              height="full"
              minHeight={rem(32)}
              padding={`0 ${rem(8)}`}
              borderWidth={rem(1)}
              borderStyle="solid"
              borderColor={getBorderColor()}
              borderRadius={rem(4)}
              cursor="pointer"
              backgroundColor="background-primary"
              onClick={onDatePickerOpen}
            >
              {currentYear}
            </Text>
          </PopoverTrigger>
          <PopoverContent
            width={rem(280)}
            height={rem(240)}
            padding={0}
            border={`${rem(1)} solid`}
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            backgroundColor="background-primary"
          >
            <PopoverBody padding={0}>
              {/* Header with year range and navigation */}
              <HStack
                alignItems="center"
                justifyContent="center"
                height={rem(48)}
                padding={rem(12)}
                borderBottom={`${rem(1)} solid`}
                borderBottomColor="stroke-primary"
              >
                <IconButton
                  width={rem(28)}
                  minWidth={0}
                  height={rem(28)}
                  padding={0}
                  borderRadius={rem(4)}
                  _hover={{ backgroundColor: 'whiteAlpha.200' }}
                  aria-label={t('yearNavigator.previousYearRange')}
                  backgroundColor="transparent"
                  icon={<Icon as={IconChevronLeft} boxSize={rem(14)} color="text-primary" />}
                  onClick={() => {
                    const newDate = subYears(selectedDate, 12);
                    updateSelectedDate(newDate);
                  }}
                  variant="ghost"
                />

                <Text minWidth={rem(100)} color="text-primary" fontSize="md" fontWeight="600" textAlign="center">
                  {format(subYears(selectedDate, 6), 'yyyy')} - {format(addYears(selectedDate, 5), 'yyyy')}
                </Text>

                <IconButton
                  width={rem(28)}
                  minWidth={0}
                  height={rem(28)}
                  padding={0}
                  borderRadius={rem(4)}
                  _hover={{ backgroundColor: 'whiteAlpha.200' }}
                  aria-label={t('yearNavigator.nextYearRange')}
                  backgroundColor="transparent"
                  icon={<Icon as={IconChevronRight} boxSize={rem(14)} color="text-primary" />}
                  onClick={() => {
                    const newDate = addYears(selectedDate, 12);
                    updateSelectedDate(newDate);
                  }}
                  variant="ghost"
                />
              </HStack>

              {/* Years grid */}
              <Box padding={rem(12)}>
                <HStack justify="center" flexWrap="wrap" spacing={0}>
                  {Array.from({ length: 12 }, (_, index) => {
                    const yearDate = addYears(subYears(selectedDate, 6), index);
                    const year = format(yearDate, 'yyyy');
                    const isSelected = format(selectedDate, 'yyyy') === year;
                    const isCurrentYear = format(new Date(), 'yyyy') === year;

                    return (
                      <Flex key={year} justifyContent="center" width="33.33%" paddingX={rem(3)} paddingY={rem(4)}>
                        <Text
                          alignItems="center"
                          justifyContent="center"
                          display="flex"
                          width={rem(60)}
                          height={rem(30)}
                          color={isSelected ? 'white' : 'text-primary'}
                          fontSize="xs"
                          fontWeight={isSelected ? '600' : '500'}
                          border={`${rem(1)} solid ${isCurrentYear ? 'blue.300' : 'transparent'}`}
                          borderRadius={rem(4)}
                          _hover={{
                            backgroundColor: isSelected ? 'blue.600' : 'whiteAlpha.200',
                            borderColor: isSelected ? 'blue.400' : 'whiteAlpha.300',
                          }}
                          cursor="pointer"
                          transition="all 0.2s ease"
                          backgroundColor={isSelected ? 'blue.500' : 'transparent'}
                          onClick={() => {
                            updateSelectedDate(yearDate);
                            onDatePickerClose();
                          }}
                        >
                          {year}
                        </Text>
                      </Flex>
                    );
                  })}
                </HStack>
              </Box>
            </PopoverBody>
          </PopoverContent>
        </Popover>

        <Tooltip label={t('yearNavigator.nextYear')} placement="top">
          <IconButton
            width={rem(32)}
            minWidth={0}
            height="full"
            margin={0}
            padding={0}
            borderWidth={rem(1)}
            borderStyle="solid"
            borderColor="stroke-primary"
            borderRadius={rem(4)}
            aria-label={t('yearNavigator.nextYear')}
            backgroundColor="background-primary"
            icon={<Icon as={IconChevronRight} boxSize={rem(14)} />}
            onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, 'NEXT')}
            size="xs"
            variant="ghost"
          />
        </Tooltip>
      </HStack>
    );
  }

  return (
    <HStack
      position="relative"
      justifyContent="center"
      gap={rem(5)}
      width="fit-content"
      minWidth="fit-content"
      height="full"
      paddingInlineEnd={rem(8)}
      paddingInlineStart={rem(8)}
    >
      <Tooltip label={t('yearNavigator.previousYear')} placement="top">
        <IconButton
          width={rem(32)}
          minWidth={0}
          height="full"
          margin={0}
          padding={0}
          borderWidth={rem(1)}
          borderStyle="solid"
          borderColor="stroke-primary"
          borderRadius={rem(4)}
          aria-label={t('yearNavigator.previousYear')}
          aspectRatio={1}
          backgroundColor="background-primary"
          icon={<Icon as={IconChevronLeft} boxSize={rem(14)} />}
          onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, 'PREV')}
          size="xs"
          variant="ghost"
        />
      </Tooltip>

      <Text
        textStyle={{ base: 'mediumBlack', lg: 'largeBlack' }}
        alignItems="center"
        justifyContent="center"
        display="flex"
        minWidth={rem(80)}
        height="full"
        minHeight={rem(32)}
        padding={`0 ${rem(8)}`}
        borderWidth={rem(1)}
        borderStyle="solid"
        borderColor={getBorderColor()}
        borderRadius={rem(4)}
        cursor="pointer"
        backgroundColor="background-primary"
      >
        {currentYear}
      </Text>

      <Tooltip label={t('yearNavigator.nextYear')} placement="top">
        <IconButton
          width={rem(32)}
          minWidth={0}
          height="full"
          margin={0}
          padding={0}
          borderWidth={rem(1)}
          borderStyle="solid"
          borderColor="stroke-primary"
          borderRadius={rem(4)}
          aria-label={t('yearNavigator.nextYear')}
          aspectRatio={1}
          backgroundColor="background-primary"
          icon={<Icon as={IconChevronRight} boxSize={rem(14)} />}
          onClick={() => onHandlePrevNextCalendarNavigation(selectedDate, 'NEXT')}
          size="xs"
          variant="ghost"
        />
      </Tooltip>
    </HStack>
  );
};

export default YearNavigator;
